import * as THREE from 'three';

/**
 * The baked shell of a close-up room (the hideout, Sid's back room, the lock-up yard): the floor, walls and
 * ceiling as textured quads with a second UV set carrying a Cycles lightmap (the bulb's bounce and a share of
 * its direct light with real soft shadows) and an ambient-occlusion map (the corners, under the furniture).
 *
 * The layout comes from art/models/blender/interior_bake.py (`npm run blender:interior`), which bakes the maps
 * and writes src/render/interiors/<room>.json: every quad's four corners in the game's metres (p0 p1 p2 p3,
 * u along p0 → p1, v along p0 → p3, counter-clockwise seen from inside) and its rect in the lightmap. This
 * module turns that into one mesh with a group per material, so Blender and three.js can never disagree about
 * where a wall is or which texels light it. The period tiles (wallpaper, lino, boards, brick, the door) and the
 * two maps are KTX2 (scripts/build-interior-textures.mjs → public/textures/interior-*.ktx2), loaded while the
 * room is up and disposed with it: flat placeholders stand in until they arrive, so nothing recompiles.
 *
 * three.js adds the lightmap to the indirect light (MeshStandardMaterial: irradiance += lightMap × intensity,
 * then × albedo / π) and multiplies the indirect light by the AO; the room's own dynamic lights stay as they
 * are. A lightmap texel of 1 at intensity π is one unit of irradiance, the same as a white hemisphere light.
 */

export interface ShellSurface {
  name: string;
  mat: string;
  /** Four corners, game frame (x right, y up, z toward the eye). */
  p: number[][];
  /** [u0, v0, u1, v1] in the lightmap. */
  lm: number[];
  /** The rect is turned a quarter: the quad's u runs up the lightmap's v. */
  rot: boolean;
  /**
   * For a tile stretched over the quad: the part of the tile it shows, [u0, v0, u1, v1] (default the whole
   * tile). A roller door raised half way shows only the top of its tile over its remaining quad.
   */
  uv?: number[];
}

export interface ShellLayout {
  room: string;
  px: number;
  texelsPerMetre: number;
  surfaces: ShellSurface[];
}

export interface ShellMaterialSpec {
  /** The tile's KTX2 name (public/textures/interior-<tile>.ktx2); none: a plain colour. */
  tile?: string;
  /** The tile's size in metres (square), or [w, h] for one that is stretched over the quad (uv 0..1). */
  size?: number | [number, number];
  /** The tile's mean colour, shown until it has loaded. */
  mean?: number;
  /** Multiplied with the tile (a dado's paint over the distemper tile); white for a coloured tile. */
  color: number;
  roughness?: number;
  metalness?: number;
  envMapIntensity?: number;
}

export interface ShellOptions {
  specs: Record<string, ShellMaterialSpec>;
  lightMapIntensity?: number;
  aoMapIntensity?: number;
  /** Load the maps and tiles (false in tests and when the renderer has no KTX2: placeholders only). */
  load?: boolean;
}

export interface InteriorShell {
  readonly mesh: THREE.Mesh;
  readonly materials: THREE.MeshStandardMaterial[];
  /** Resolves when every file is in (or has failed and its placeholder stays). */
  readonly ready: Promise<boolean>;
  /** The maps that arrived (tests, the debug state). */
  loaded: string[];
  setLightMapIntensity(k: number): void;
  dispose(): void;
}

const BASE = import.meta.env?.BASE_URL ?? './';

function flat(r: number, g: number, b: number, srgb: boolean): THREE.DataTexture {
  const d = new Uint8Array(4 * 4 * 4);
  for (let i = 0; i < d.length; i += 4) d.set([r, g, b, 255], i);
  const t = new THREE.DataTexture(d, 4, 4);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.needsUpdate = true;
  return t;
}

function hexBytes(c: number): [number, number, number] {
  return [(c >> 16) & 255, (c >> 8) & 255, c & 255];
}

/** Build the shell mesh for a layout: one draw call per material, the maps loading in the background. */
export function buildInteriorShell(layout: ShellLayout, opts: ShellOptions): InteriorShell {
  const keys = [...new Set(layout.surfaces.map((s) => s.mat))];
  const pos: number[] = [];
  const nrm: number[] = [];
  const uv: number[] = [];
  const uv1: number[] = [];
  const col: number[] = [];
  const groups: { start: number; count: number; index: number }[] = [];
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const n = new THREE.Vector3();
  const c = new THREE.Color();
  for (const key of keys) {
    const spec = opts.specs[key];
    if (!spec) throw new Error(`interior shell: no material spec for '${key}'`);
    const start = pos.length / 3;
    c.setHex(spec.color);
    for (const s of layout.surfaces) {
      if (s.mat !== key) continue;
      const [p0, p1, p2, p3] = s.p.map((p) => new THREE.Vector3(p[0], p[1], p[2])) as [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3];
      a.subVectors(p1, p0);
      b.subVectors(p3, p0);
      n.crossVectors(a, b).normalize();
      const w = a.length();
      const h = b.length();
      const [tu, tv] = !spec.size ? [1, 1] : typeof spec.size === 'number' ? [spec.size, spec.size] : spec.size;
      // uv0 in tiles (metres over the tile's size; a stretched tile spans the quad once), uv1 the lightmap rect
      const [u0, v0, u1, v1] = s.lm as [number, number, number, number];
      const lm: [number, number][] = s.rot ? [[u0, v1], [u0, v0], [u1, v0], [u1, v1]] : [[u0, v0], [u1, v0], [u1, v1], [u0, v1]];
      const corners: [THREE.Vector3, number, number, number, number][] = [
        [p0, 0, 0, lm[0]![0], lm[0]![1]],
        [p1, w / tu, 0, lm[1]![0], lm[1]![1]],
        [p2, w / tu, h / tv, lm[2]![0], lm[2]![1]],
        [p3, 0, h / tv, lm[3]![0], lm[3]![1]],
      ];
      const stretched = !!spec.size && typeof spec.size !== 'number';
      const [su0, sv0, su1, sv1] = (s.uv ?? [0, 0, 1, 1]) as [number, number, number, number];
      for (const i of [0, 1, 2, 0, 2, 3]) {
        const [p, u0, v0, lu, lv] = corners[i]!;
        pos.push(p.x, p.y, p.z);
        nrm.push(n.x, n.y, n.z);
        uv.push(stretched ? (i === 0 || i === 3 ? su0 : su1) : u0, stretched ? (i < 2 ? sv0 : sv1) : v0);
        uv1.push(lu, lv);
        col.push(c.r, c.g, c.b);
      }
    }
    groups.push({ start, count: pos.length / 3 - start, index: groups.length });
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geo.setAttribute('uv1', new THREE.Float32BufferAttribute(uv1, 2));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  for (const g of groups) geo.addGroup(g.start, g.count, g.index);
  geo.computeBoundingSphere();

  // placeholders: the tile's mean, no bounce yet, no occlusion yet
  const disposables: { dispose(): void }[] = [geo];
  const light = flat(0, 0, 0, true);
  const ao = flat(255, 255, 255, false);
  light.channel = ao.channel = 1;
  light.wrapS = light.wrapT = ao.wrapS = ao.wrapT = THREE.ClampToEdgeWrapping;
  disposables.push(light, ao);
  const materials = keys.map((key) => {
    const spec = opts.specs[key]!;
    const map = spec.tile ? flat(...hexBytes(spec.mean ?? 0x808080), true) : null;
    if (map) disposables.push(map);
    const m = new THREE.MeshStandardMaterial({
      vertexColors: true,
      map,
      lightMap: light,
      lightMapIntensity: opts.lightMapIntensity ?? Math.PI,
      aoMap: ao,
      aoMapIntensity: opts.aoMapIntensity ?? 1,
      roughness: spec.roughness ?? 0.9,
      metalness: spec.metalness ?? 0,
      envMapIntensity: spec.envMapIntensity ?? 1,
    });
    m.name = `shell-${layout.room}-${key}`;
    disposables.push(m);
    return m;
  });
  const mesh = new THREE.Mesh(geo, materials);
  mesh.name = `shell-${layout.room}`;
  mesh.receiveShadow = true;

  const shell: InteriorShell = {
    mesh,
    materials,
    loaded: [],
    ready: Promise.resolve(false),
    setLightMapIntensity(k) {
      for (const m of materials) m.lightMapIntensity = k;
    },
    dispose() {
      for (const d of disposables) d.dispose();
      disposables.length = 0;
    },
  };
  let disposed = false;
  const origDispose = shell.dispose;
  shell.dispose = (): void => {
    disposed = true;
    origDispose();
  };

  if (opts.load !== false) {
    (shell as { ready: Promise<boolean> }).ready = (async (): Promise<boolean> => {
      let any = false;
      try {
        const { ktx2Loader } = await import('./ktx2');
        const loader = ktx2Loader();
        const fetch = async (name: string, srgb: boolean, repeat: boolean): Promise<THREE.Texture | null> => {
          try {
            const t = await loader.loadAsync(`${BASE}textures/interior-${name}.ktx2`);
            t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
            t.wrapS = t.wrapT = repeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
            t.anisotropy = repeat ? 8 : 2;
            t.needsUpdate = true;
            return t;
          } catch {
            return null;
          }
        };
        const tiles = [...new Set(keys.map((k) => opts.specs[k]!.tile).filter((t): t is string => !!t))];
        const [lightTex, aoTex, ...tileTex] = await Promise.all([
          fetch(`${layout.room}-light`, true, false),
          fetch(`${layout.room}-ao`, false, false),
          ...tiles.map((t) => fetch(t, true, true)),
        ]);
        if (disposed) {
          for (const t of [lightTex, aoTex, ...tileTex]) t?.dispose();
          return false;
        }
        const byTile = new Map(tiles.map((t, i) => [t, tileTex[i] ?? null]));
        if (lightTex) {
          lightTex.channel = 1;
          disposables.push(lightTex);
          shell.loaded.push('light');
        }
        if (aoTex) {
          aoTex.channel = 1;
          disposables.push(aoTex);
          shell.loaded.push('ao');
        }
        materials.forEach((m, i) => {
          const spec = opts.specs[keys[i]!]!;
          if (lightTex) m.lightMap = lightTex;
          if (aoTex) m.aoMap = aoTex;
          const t = spec.tile ? byTile.get(spec.tile) : null;
          if (t) {
            // a tile that spans its quad once must not repeat past its edge
            if (spec.size && typeof spec.size !== 'number') t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
            m.map = t;
            if (!shell.loaded.includes(spec.tile!)) {
              shell.loaded.push(spec.tile!);
              disposables.push(t);
            }
          }
          any = any || !!(lightTex || aoTex || t);
        });
      } catch {
        // no KTX2 (tests, an old GPU): the placeholders stay
      }
      return any;
    })();
  }
  return shell;
}
